/**
 * OTel semantic convention(exception.*) 기반 에러 필드 빌더.
 *
 * - stacktrace 는 8KB 로 truncate (ES keyword 매핑 거부/절단 보호)
 * - message/stacktrace 는 전송 전 PII(이메일·토큰 패턴) 마스킹
 * - fingerprint: type + 스택 첫 프레임의 결정적 해시 → Kibana 에서
 *   "동일 에러 묶어 카운트" 용도. 외부 의존성 없이 djb2 사용.
 */

export interface ExceptionFields {
  exception: {
    type: string;
    message: string;
    stacktrace?: string;
  };
  error: {
    /** 동일 에러 집계용 결정적 해시 (exception.type + 스택 첫 프레임) */
    fingerprint: string;
  };
}

const MAX_STACKTRACE_LENGTH = 8 * 1024;

const EMAIL_PATTERN = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
const BEARER_PATTERN = /bearer\s+[\w\-.~+/]+=*/gi;
const SECRET_PARAM_PATTERN =
  /\b(token|key|secret|password|session)=[^&\s"']+/gi;

export function maskPii(text: string): string {
  return text
    .replace(EMAIL_PATTERN, '[email]')
    .replace(BEARER_PATTERN, 'Bearer [token]')
    .replace(SECRET_PARAM_PATTERN, '$1=[masked]');
}

/** djb2 — 의존성 없는 결정적 단문 해시 (32bit hex) */
function djb2(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i += 1) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

function firstStackFrame(stack: string | undefined): string {
  if (!stack) {
    return '';
  }
  const frame = stack
    .split('\n')
    .find((line) => line.trimStart().startsWith('at '));
  return frame?.trim() ?? '';
}

function fingerprintOf(
  type: string,
  stack: string | undefined,
  message: string,
): string {
  return djb2(`${type}|${firstStackFrame(stack) || message}`);
}

export function exceptionFieldsFromError(error: Error): ExceptionFields {
  const type = error.name || 'Error';
  const stack =
    typeof error.stack === 'string'
      ? maskPii(error.stack.slice(0, MAX_STACKTRACE_LENGTH))
      : undefined;
  return {
    exception: {
      type,
      message: maskPii(error.message),
      stacktrace: stack,
    },
    error: { fingerprint: fingerprintOf(type, error.stack, error.message) },
  };
}

/** Error 가 아닌 값(문자열/객체 rejection 등)용. 직렬화 실패도 안전 처리 */
export function exceptionFieldsFromValue(
  value: unknown,
  type: string,
): ExceptionFields {
  let message: string;
  if (typeof value === 'string') {
    message = value;
  } else {
    try {
      message = JSON.stringify(value) ?? String(value);
    } catch {
      message = String(value);
    }
  }
  message = maskPii(message);
  return {
    exception: { type, message },
    error: { fingerprint: fingerprintOf(type, undefined, message) },
  };
}
